// **************** common import *******************
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "../../../utils/redirections";

// ********************* common function
import { WordTitle } from "../../../utils/commonFunction";

// *****************************
import Table from "../../../components/table/table";

// ******************************** API
import { FetchNotesErrorList } from "../../../redux/slices/noteserror.slice";
import { getSenderNameList,getNoteTypeList } from "../../../api/list";

// ********************************* inter face  ********************************
import { sortInterFace } from "../../../utils/types/interFace";

// ICON
import { FiFileText } from "react-icons/fi";
import { IoWarningOutline } from "react-icons/io5";
import { MdSchedule } from "react-icons/md";
import AIIcon from "../../../assets/icons/AIIcon.png";

import { ReactSelectInput } from "../../../components/select/react-select";
import { formatDateMMDDYYYY } from "../../../utils";
import NotesAISummarizeModal from "./notes-ai-summarize";

interface NotesTabProps {
  patientId: string | number;
}

const NotesTab: React.FC<NotesTabProps> = ({ patientId }) => {
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState<boolean>(true);
  const NotesErrorData = useSelector(
    (state: any) => state?.NotesErrorList?.NotesErrorList || {}
  );

  // search
  const [senderName, setSenderName] = useState<any>([]);
  const [selectedSenderName, setSelectedSenderName] = useState<any>("");
  const [selectedDos, setSelectedDos] = useState<null | string>("");
  const [noteType, setNoteType] = useState<any>([]);
  const [selectedNoteType, setSelectedNoteType] = useState<any>("");

  //select
  const [selectedRows,setSelectedRows] = useState<any>([])

  // modal
  const [showAISummarizeModal, setShowAISummarizeModal] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [todoExtraction, setTodoExtraction] = useState<boolean>(false);

  // sort
  const [sort, setSort] = useState<sortInterFace>({ created_at: "desc" });

  const [limit] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);

  // handle the loading the data
  const handleNotesErrorData = (
    load: boolean,
    sorting: any = {},
    pageNumber?: number,
    dos?: string | null,
    senderName?: string | null,
    noteType?: string | null
  ) => {
    pageNumber && setPage(pageNumber);
    if (pageNumber || page === 1) {
      setLoader(load);
    }

    dispatch(
      FetchNotesErrorList({
        page: pageNumber || page,
        limit,
        sort_by:
          Object.keys(sorting).length !== 0
            ? Object.keys(sorting)[0]
            : "created_at",
        sort_dir:
          Object.keys(sorting).length !== 0
            ? sorting[Object.keys(sorting)[0]]
            : "desc",
        date_of_service: dos === null ? null : dos || selectedDos,
        sender_name: senderName === null ? null : senderName || selectedSenderName,
        notes_type: noteType === null ? null : noteType || selectedNoteType,
        patient_id: patientId, // Add patient_id parameter
      })
    ).then((res: any) => {
      setTotalPage(res?.payload?.result?.total_pages);
      setLoader(false);
    });
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const redirectNotesPage = (row: any) => {
    window.open(`/notes/${row?.id}`, "_blank");
  };

  const handleSenderName = async () => {
    try {
      const result = await getSenderNameList();
      if (result?.result) {
        const senderNameOptions = result?.result?.map((item: any) => ({
          label: item,
          value: item,
        }));
        setSenderName(senderNameOptions);
      }
    } catch (error) {
      console.error("Error fetching sender names:", error);
    }
  };

  const handleNoteType = async () => {
    try {
      const result = await getNoteTypeList();
      if (result?.result) {
        const noteTypeOptions = result?.result?.map((item: any) => ({
          label: item,
          value: item,
        }));
        setNoteType(noteTypeOptions);
      }
    } catch (error) {
      console.error("Error fetching note types:", error);
    }
  };

  const handleShowAISummarizeModal = (isTodoExtraction = false) => {
    setTodoExtraction(isTodoExtraction);
    // Check if all notes are selected
    const allNotesSelected = selectedRows.length
    === NotesErrorData.length && NotesErrorData.length > 0;

    if (allNotesSelected) {
      // Show warning modal first
      setShowWarningModal(true);
    } else {
      // Show AI summarize modal directly
      setShowAISummarizeModal(true);
    }
  };

  const handleCloseAISummarizeModal = () => {
    setShowAISummarizeModal(false);
    setTodoExtraction(false);
    setSelectedRows([]);
  };

  const handleTodoPrompt = () => {
    handleShowAISummarizeModal(true);
  };

  const handleCloseWarningModal = () => {
    setShowWarningModal(false);
    setTodoExtraction(false);
  };

  const handleContinueWarning = () => {
    setShowWarningModal(false);
    setShowAISummarizeModal(true);
  };

  // all use effect function
  useEffect(() => {
    handleSenderName();
    handleNoteType();
  }, []);

  useEffect(() => {
    handleNotesErrorData(true, sort);
  }, [page, patientId]);

  const handleSenderNameChange = (selectedOption: any) => {
    setSelectedSenderName(selectedOption ? selectedOption.value : "");
    handleNotesErrorData(
      true,
      sort,
      1,
      selectedDos,
      selectedOption ? selectedOption.value : null,
      selectedNoteType
    );
  };

  const handleNoteTypeChange = (selectedOption: any) => {
    setSelectedNoteType(selectedOption ? selectedOption.value : "");
    handleNotesErrorData(
      true,
      sort,
      1,
      selectedDos,
      selectedSenderName,
      selectedOption ? selectedOption.value : null
    );
  };

  return (
    <div className="p-4 pt-2 rounded-lg bg-gray-50 dark:bg-gray-800">
      <div className="mt-2 grid grid-cols-12 gap-1">
        <div className="col-span-4 flex items-center">
          <h6 className="text-[#1C0F03] font-[600] text-[20px]">Notes</h6>
        </div>
       
        <div className="col-span-8 flex gap-[16px] items-center justify-end mb-[4px] mt-2">
          <ReactSelectInput
            name="senderName"
            onChange={(e: any) => {
              handleSenderNameChange(e);
            }}
            options={senderName}
            placeholder="Select Sender Name"
            className="w-[250px]"
            isSearchable={true}
            isClearable={true}
            isDisabled={false}
          />
          <ReactSelectInput
            name="noteType"
            onChange={(e: any) => {
              handleNoteTypeChange(e);
            }}
            options={noteType}
            placeholder="Select Note Type"
            className="w-[250px]"
            isSearchable={true}
            isClearable={true}
            isDisabled={false}
          />

          <input
            type="text"
            placeholder="Select DOS"
            onFocus={(e) => (e.target.type = "date")}
            onBlur={(e) => {
              if (!e.target.value) e.target.type = "text";
            }}
            value={selectedDos ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedDos(value);
              handleNotesErrorData(true, sort, 1, formatDateMMDDYYYY(value));
            }}
            className="w-[250px] h-[40px] border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {selectedDos && selectedDos !== "" ? (
            <p
              className="text-[red] cursor-pointer"
              onClick={(e) => {
                setSelectedDos(null);
                handleNotesErrorData(true, sort, 1, null);
              }}
            >
              Clear
            </p>
          ) : null}
        </div>

        <div className="col-span-12">
          <Table
            loading={loader}
            data={NotesErrorData}
            tableScroll={"100%"}
            sort={true}
            Clickable={true}
            showCheckbox={true}
            // showSelectAllCheckbox = {false}
            selectedRows={selectedRows}
            MultiOperationButtonIconComponent={<img src={AIIcon} alt="AI" className="w-5 h-5" />}
            showModalSelectedAbove = {0}
            MultiDeletedFucn = {() => handleShowAISummarizeModal(false)}
            MultiOperationButtonIconClass = "bg-white hover:bg-teal-50 disabled:bg-white disabled:opacity-50 border-[#17be62] hover:border-[#14a085]"
            setSelectedRows = {setSelectedRows}
            MultiOperationButton = {'Summarize'}
            MultiOperationButton2 = {'To Do'}
            MultiOperationButton2Func = {() => handleTodoPrompt()}
            MultiOperationButton2IconComponent = {<img src={AIIcon} alt="AI" className="w-5 h-5" />}
            MultiOperationButton2IconClass = "bg-white hover:bg-teal-50 disabled:bg-white disabled:opacity-50 border-[#17be62] hover:border-[#14a085]"
            rowClick={(row: any) => {
              redirectNotesPage(row);
            }}
            hasNextPage={page < totalPage}
            NextPageData={handleNextPage}
            columns={["Sender", "DOS", "Note Type", "Action"]}
            sortTitleKeys={{
              Sender: "sender_name",
              DOS: "encounter_date",
              "Note Type": "note_type",
            }}
            handleSortfunc={(key: any, value: any) => {
              setSort({ [key]: value });
              page === 1
                ? handleNotesErrorData(false, { [key]: value })
                : handleNotesErrorData(false, { [key]: value }, 1);
            }}
            scopedSlots={{
              Sender: ({ row }: any) => (
                <td title={row?.sender_name || ""}>
                  <div className="max-w-[200px] truncate cursor-pointer">
                    {WordTitle(row?.sender_name || "")}
                  </div>
                </td>
              ),
              DOS: ({ row }: any) => (
                <td title={row?.encounter_date || ""}>
                  <div className="max-w-[150px] truncate cursor-pointer">
                    {row?.encounter_date || ""}
                  </div>
                </td>
              ),
              "Note Type": ({ row }: any) => (
                <td title={row?.note_type || ""}>
                  <div className="max-w-[150px] truncate cursor-pointer">
                    {WordTitle(row?.note_type || "")}
                  </div>
                </td>
              ),
              Action: ({ row }: any) => {
                return (
                  <td>
                    <div className="flex gap-[16px] items-center justify-center pr-[4px]">
                      <a
                        href={`/notes/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="no-underline"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <FiFileText
                          fontWeight={900}
                          fontSize={22}
                          title="Notes Content"
                        />
                      </a>
                    </div>
                  </td>
                );
              },
            }}
          />
        </div>
      </div>

      <NotesAISummarizeModal
        show={showAISummarizeModal}
        patientId={patientId}
        selectedNotesIds={selectedRows.map((row: any) => String(row.id))}
        todoExtraction={todoExtraction}
        onClose={handleCloseAISummarizeModal}
      />

      {/* Warning Modal for All Notes Selected */}
      {showWarningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 transform transition-all animate-slideUp">
            {/* Icon and Title Section */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative mb-4">
                {/* Animated background circle */}
                <div className="absolute inset-0 bg-amber-100 rounded-full animate-pulse"></div>
                {/* Warning Icon */}
                <div className="relative bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-4 shadow-lg">
                  <IoWarningOutline className="w-12 h-12 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center">
                Processing All Notes
              </h2>
            </div>

            {/* Message Section */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <MdSchedule className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-800 font-medium mb-2">
                    All {selectedRows.length} notes are selected
                  </p>
                  <p className="text-gray-700 
                  text-sm 
                  leading-relaxed">
                    Since all notes are selected, the processing time may take a while. 
                    The AI summarization will analyze all notes in detail.
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <p className="text-center text-gray-600 text-sm mb-6">
              Do you still want to continue with the summarization?
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleCloseWarningModal}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={handleContinueWarning}
                className="px-6 py-3 text-white bg-gradient-to-r from-[#083B3E] to-[#0a4d51] hover:from-[#0a4d51] hover:to-[#0c5f64] rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2"
              >
                <span>Continue</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NotesTab;